import { OpenFin } from '@openfin/core'
import { withProfiler } from '@sentry/react'
import { ComponentType, Suspense, useCallback, useEffect } from 'react'
import { Route, Routes, useHref, useNavigate } from 'react-router'
import {
  MAIN_WINDOW_NAME,
  subscribeExternalIsinSearch
} from '../containers/OpenFin/helpers'
import { clearStoredOidcUser } from '../helpers/auth'
import { useMainWindowOpenFinEffects } from '../helpers/hooks/useMainWindowOpenFinEffects'
import { useResetStoredSettings } from '../helpers/hooks/useResetStoredSettings'
import { clearAllHydration } from '../helpers/hydration'
import { clearStoredSelectedUser } from '../helpers/selectedUser'
import { storeTheme } from '../helpers/theme'

import AdminRouteParent from '../routing/Admin/AdminRouteParent'

import Loading from '../components/Loading/Loading'
import Toasts from '../components/Toasts/Toasts'

import TickerRating from '../components/Admin/TickerRating/TickerRating'
import { AggressorAndLastLook } from '../components/AggressorAndLastLook/AggressorAndLastLook'
import Header from '../components/Header/Header'
import Logger from '../components/Logger/Logger'
import { openTickerRating } from '../store/admin/tickerRating/actions'

import {
  applySavedSnapshot,
  cleanupDetachedView,
  getIsSigningOut,
  handleBoundsChanged,
  saveSnapshotDebounced,
  useOpenFin
} from '../helpers/hooks/useOpenFin'
import {
  ActivityLogGrid,
  ActivityTicker,
  AggressAttemptsGrid,
  AlertFilterEditor,
  BBMISIN,
  BenchmarkDataContainer,
  CreateTradingList,
  ExternalLiquidityStats,
  ManagementReportMainPage,
  OpenFinAggressor,
  OpenFinDepth,
  OpenFinLastLook,
  OpenFinRFQ,
  OpenFinTradeConfirm,
  OpenFinUserEditor,
  OpenFinWatchlist,
  PassiveOrders,
  QueueStats,
  RestrictedTrades,
  RFQManager,
  SecuritiesTable,
  SettingsModal,
  SystemParameters,
  Tiers,
  TradeBlotterGrid,
  TradeWatchList,
  TradingListLayout,
  UserDashboard,
  UserData,
  WatchlistManager
} from './lazyRoutes'

import { getIsSignedIn } from '../store/auth/selectors'
import { addLogItem } from '../store/log/actions'
import { getTradeConfirmId } from '../store/order/selectors'
import { useAppDispatch, useAppSelector } from '../store/types'
import { getUseLightTheme } from '../store/userPreferences/selectors'
import {
  getCanMarkRestricted,
  getCanSeeSystemParamEditor,
  getIsAdmin,
  getWebSettingsLoaded
} from '../store/webSettings/selectors'
import {
  aggressorWindowClosed,
  lastLookWindowClosed
} from '../store/windows/actions'

type ScreenName =
  | 'ActivityLog'
  | 'ActivityTicker'
  | 'AggressAttempts'
  | 'BBMISIN'
  | 'ExternalLiquidityStats'
  | 'ListTrading'
  | 'ManagementReports'
  | 'PassiveOrders'
  | 'QueueStats'
  | 'RestrictedTrading'
  | 'RFQManager'
  | 'SystemParameters'
  | 'TickerRating'
  | 'Tiers'
  | 'TradeBlotter'
  | 'UserDashboard'
  | 'UserData'
  | 'WatchlistManager'

type Props = {
  SecureRoute: ComponentType
}

let shutdownHandlerRegistered = false

const App = ({ SecureRoute }: Props) => {
  const dispatch = useAppDispatch()
  const openFin = useOpenFin()
  const { finWindowAction, isOpenFin, openPlatformWindow } = openFin
  const isAdmin = useAppSelector(getIsAdmin)
  const canSeeSystemParamEditor = useAppSelector(getCanSeeSystemParamEditor)
  const canSeeRestrictMenu = useAppSelector(getCanMarkRestricted)
  const useLightTheme = useAppSelector(getUseLightTheme)
  const isSignedIn = useAppSelector(getIsSignedIn)
  const tradeConfirmId = useAppSelector(getTradeConfirmId)
  const webSettingsLoaded = useAppSelector(getWebSettingsLoaded)

  const externalLiquidityStatsUrl = useHref('/admin/ExternalLiquidityStats')
  const userDataUrl = useHref('/admin/UserData')
  const aggressAttemptsUrl = useHref('/admin/AggressAttempts')
  const rfqManagerUrl = useHref('/RFQManager')
  const listTradingUrl = useHref('/ListTrading')
  const passiveOrdersUrl = useHref('/PassiveOrders')

  useResetStoredSettings(true)

  const documentClick = (e: MouseEvent) => {
    // @ts-ignore
    const testId = e.target?.getAttribute('data-testid')

    if (testId) {
      const st = 'click ' + testId
      dispatch(addLogItem(st))
    }
  }

  const documentKeyUp = (e: KeyboardEvent) => {
    if (e.target instanceof Element) {
      const testId = e.target?.getAttribute('data-testid') || ''
      const st = 'KeyUp ' + e.key + ' ' + testId
      // console.log(st)
      dispatch(addLogItem(st))
    }
  }

  useEffect(() => {
    document.addEventListener('click', (e) => documentClick(e))
    document.addEventListener('keyup', (e) => documentKeyUp(e))

    return () => {
      document.removeEventListener('click', (e) => documentClick(e))
      document.removeEventListener('keyup', (e) => documentKeyUp(e))
    }
  }, [])

  useEffect(() => {
    if (
      !isOpenFin ||
      !webSettingsLoaded ||
      fin.me.identity.name !== MAIN_WINDOW_NAME
    ) {
      return
    }
    // A fresh main-window boot starts a new session: clear persisted/shared data
    clearStoredSelectedUser()
    clearAllHydration()
    void applySavedSnapshot()
    /*  localStorage token is shared with popouts.
        Wipe it on shutdown so it doesn't outlive the app run. */
    if (!shutdownHandlerRegistered) {
      shutdownHandlerRegistered = true
      void fin.System.registerShutdownHandler((event) => {
        clearStoredOidcUser()
        clearStoredSelectedUser()
        clearAllHydration()
        event.proceed()
      }).catch((err) => {
        shutdownHandlerRegistered = false
        console.warn('Failed to register shutdown handler', err)
      })
    }
    const app = fin.Application.getCurrentSync()
    const onViewDetached = (
      event: OpenFin.ApplicationEvents.ByType<'window-view-detached'>
    ) => {
      // Sign-out tears down docked views itself — leave snapshot + store alone.
      if (getIsSigningOut()) return
      saveSnapshotDebounced()
      /*  target = window it was reattached to; null = destroyed.
          Only destroyed views clear store state. */
      if (event.target !== null) return
      cleanupDetachedView(event.viewIdentity.name, dispatch)
    }
    /* Catch popouts closed from the OS (taskbar, Alt+F4) so undecided orders
       don't stay live server-side; the epics no-op once a decision was made.
       Listened app-level in the main window because the Aggressor opener (a
       Depth popout) can be gone by the time the Aggressor window closes. */
    const onWindowClosed = (
      event: OpenFin.ApplicationEvents.ByType<'window-closed'>
    ) => {
      if (getIsSigningOut()) return
      const [baseName, ...rest] = event.name.split('/')
      const id = rest.join('/')
      if (!id) return
      if (baseName === 'Aggressor') {
        dispatch(aggressorWindowClosed(rest[0]))
      } else if (baseName === 'LastLook') {
        dispatch(lastLookWindowClosed(id))
      }
    }
    void app.addListener('window-bounds-changed', handleBoundsChanged)
    void app.addListener('window-view-attached', saveSnapshotDebounced)
    void app.addListener('window-view-detached', onViewDetached)
    void app.addListener('window-closed', onWindowClosed)
    return () => {
      void app.removeListener('window-bounds-changed', handleBoundsChanged)
      void app.removeListener('window-view-attached', saveSnapshotDebounced)
      void app.removeListener('window-view-detached', onViewDetached)
      void app.removeListener('window-closed', onWindowClosed)
    }
  }, [isOpenFin, webSettingsLoaded, dispatch])

  useEffect(() => {
    if (!isOpenFin || fin.me.identity.name !== MAIN_WINDOW_NAME) return
    return subscribeExternalIsinSearch(dispatch)
  }, [isOpenFin, dispatch])

  useMainWindowOpenFinEffects(openFin)

  useEffect(() => {
    isOpenFin &&
      tradeConfirmId &&
      finWindowAction('TradeConfirm', 'close', true).then(() =>
        openPlatformWindow(`TradeConfirm/${tradeConfirmId}`)
      )
  }, [isOpenFin, tradeConfirmId])

  useEffect(() => {
    // Hold off until signed in so the pre-auth screen keeps the default theme
    // and we don't persist the default before the user's prefs have loaded.
    if (!isSignedIn) return
    storeTheme(useLightTheme)
    useLightTheme
      ? document.body.classList.add('lightTheme')
      : document.body.classList.remove('lightTheme')
  }, [isSignedIn, useLightTheme])

  const navigate = useNavigate()

  const goToScreen = useCallback(
    (screen: ScreenName) => {
      switch (screen) {
        // non admin
        case 'TradeBlotter':
        case 'ActivityLog':
          navigate(`/${screen}`)
          break
        case 'Tiers':
        case 'QueueStats':
        case 'ManagementReports':
        case 'BBMISIN':
        case 'ActivityTicker':
        case 'WatchlistManager':
        case 'UserDashboard':
          /* intentionally falls through */
          navigate(`/admin/${screen}`)
          break
        case 'TickerRating':
          dispatch(openTickerRating())
          break
        // more strict
        case 'SystemParameters':
          if (canSeeSystemParamEditor) {
            navigate(`/admin/${screen}`)
          }
          break
        case 'RestrictedTrading':
          if (canSeeRestrictMenu) {
            navigate(`/admin/${screen}`)
          }
          break
        // ------------------ new tabs ------------------ //
        case 'PassiveOrders':
          window.open(passiveOrdersUrl)
          break
        case 'RFQManager':
          window.open(rfqManagerUrl)
          break
        case 'AggressAttempts':
          window.open(aggressAttemptsUrl)
          break
        case 'ListTrading':
          window.open(listTradingUrl, 'listTradingTab')
          break
        case 'ExternalLiquidityStats':
          window.open(externalLiquidityStatsUrl)
          break
        case 'UserData':
          window.open(userDataUrl)
          break
        default:
          navigate('')
      }
    },
    [
      navigate,
      canSeeSystemParamEditor,
      canSeeRestrictMenu,
      passiveOrdersUrl,
      rfqManagerUrl,
      aggressAttemptsUrl,
      listTradingUrl,
      externalLiquidityStatsUrl,
      userDataUrl
    ]
  )

  return (
    <>
      <Header setSelectedTab={goToScreen} isAdmin={isAdmin} />
      <Logger />
      <Toasts />
      <AggressorAndLastLook />
      <TickerRating />

      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<SecureRoute />}>
            <Route index element={<SecuritiesTable />} />
            <Route path="/PassiveOrders" element={<PassiveOrders />} />
            <Route path="/ListTrading" element={<TradingListLayout />}>
              <Route index element={<CreateTradingList />} />
              <Route path=":watchlistId" element={<TradeWatchList />} />
            </Route>
            <Route path="/TradeBlotter" element={<TradeBlotterGrid />} />
            <Route path="/ActivityLog" element={<ActivityLogGrid />} />

            {/* openfin routes */}
            <>
              <Route
                path="/Aggressor/:initialId/:securityId"
                element={<OpenFinAggressor />}
              />
              {/*
              OpenFin mount of the Alert Manager — opened in a popout window via
              this route. The web app instead renders <AlertFilterEditor> inline
              from ClientMenu; the two mounts are mutually exclusive by platform.
            */}
              <Route
                path="/AlertManager"
                element={
                  <AlertFilterEditor
                    closeFunc={() => {
                      finWindowAction('AlertManager', 'close')
                    }}
                  />
                }
              />
              <Route path="/Benchmarks" element={<BenchmarkDataContainer />} />
              <Route path="/Depth/:id" element={<OpenFinDepth />} />
              <Route path="/LastLook/:id" element={<OpenFinLastLook />} />
              <Route
                path="/RFQs/:orderId/:securityId"
                element={<OpenFinRFQ />}
              />
              <Route path="RFQManager" element={<RFQManager />} />
              <Route
                path="/Settings"
                element={
                  <SettingsModal
                    closeFunc={() => {
                      finWindowAction('Settings', 'close')
                    }}
                  />
                }
              />
              <Route
                path="/TradeConfirm/:id"
                element={<OpenFinTradeConfirm />}
              />
              <Route
                path="/Watchlists/:gridIndex"
                element={<OpenFinWatchlist />}
              />
            </>

            <Route path="/admin" element={<AdminRouteParent />}>
              <Route
                path="AggressAttempts"
                element={<AggressAttemptsGrid setSelectedTab={goToScreen} />}
              />
              <Route path="Tiers" element={<Tiers />} />
              <Route path="SystemParameters" element={<SystemParameters />} />
              <Route path="QueueStats" element={<QueueStats />} />
              <Route
                path="ManagementReports"
                element={<ManagementReportMainPage />}
              />
              <Route path="BBMISIN" element={<BBMISIN />} />
              <Route path="ActivityTicker" element={<ActivityTicker />} />
              <Route path="UserDashboard" element={<UserDashboard />} />
              <Route path="RestrictedTrading" element={<RestrictedTrades />} />
              <Route path="UserData" element={<UserData />} />

              <Route
                path="ExternalLiquidityStats"
                element={<ExternalLiquidityStats />}
              />
              <Route path="WatchlistManager" element={<WatchlistManager />} />

              {/* openfin only */}
              <Route path="UserEditor/:id" element={<OpenFinUserEditor />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}

export default withProfiler(App, { name: 'Bonds.com React app' })
